import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import DirectBookingPricingRules from '../components/directBookingPricingRules';
import { useUser } from '../context/UserContext';
import './directBooking.css';

const getPayoutStatusTone = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'connected') return 'success';
    if (normalized === 'action_required') return 'warning';
    if (normalized === 'unavailable' || normalized === 'failed' || normalized === 'error') return 'error';
    return 'muted';
};

const splitLines = (value) => String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const PHOTO_FIELDS = [
    { key: 'kitchenPhotoUrl', label: 'Kitchen' },
    { key: 'livingRoomPhotoUrl', label: 'Living Room' },
    { key: 'balconyPhotoUrl', label: 'Balcony' },
    { key: 'poolPhotoUrl', label: 'Pool' },
    { key: 'bedroomPhotoUrl', label: 'Bedroom' },
    { key: 'bathroomPhotoUrl', label: 'Bathroom' },
    { key: 'diningPhotoUrl', label: 'Dining Area' },
    { key: 'workspacePhotoUrl', label: 'Workspace' },
    { key: 'exteriorPhotoUrl', label: 'Exterior' }
];

const EMPTY_PHOTO_FORM = PHOTO_FIELDS.reduce((acc, field) => {
    acc[field.key] = '';
    return acc;
}, {});

const normalizeLabel = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const PHOTO_KEY_BY_LABEL = PHOTO_FIELDS.reduce((acc, field) => {
    acc[normalizeLabel(field.label)] = field.key;
    return acc;
}, {});

const mapGalleryToPhotoForm = (galleryEntries) => {
    const mapped = { ...EMPTY_PHOTO_FORM };
    const fallbackKeys = PHOTO_FIELDS.map((field) => field.key);

    if (!Array.isArray(galleryEntries)) return mapped;

    galleryEntries.forEach((entry) => {
        const url = typeof entry === 'string' ? entry.trim() : String(entry?.url || '').trim();
        const label = typeof entry === 'string' ? '' : String(entry?.label || '').trim();
        if (!url) return;

        const key = PHOTO_KEY_BY_LABEL[normalizeLabel(label)];
        if (key && !mapped[key]) {
            mapped[key] = url;
            return;
        }

        const fallbackKey = fallbackKeys.find((candidate) => !mapped[candidate]);
        if (fallbackKey) mapped[fallbackKey] = url;
    });

    return mapped;
};

const buildGalleryFromPhotoForm = (formValues) => {
    return PHOTO_FIELDS
        .map((field) => {
            const url = String(formValues[field.key] || '').trim();
            return url ? { url, label: field.label } : null;
        })
        .filter(Boolean);
};

const DirectBookingManagerPage = () => {
    const { propertyId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useUser();
    const [property, setProperty] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [payoutStatus, setPayoutStatus] = useState({ loading: true, state: 'muted', text: '', account: null, accountId: '' });
    const [launchingPayoutSetup, setLaunchingPayoutSetup] = useState(false);
    const [form, setForm] = useState({
        title: '',
        slug: '',
        shortDescription: '',
        longDescription: '',
        heroImageUrl: '',
        ...EMPTY_PHOTO_FORM,
        amenitiesText: '',
        houseRules: '',
        automatedGuestMessage: '',
        nightlyRate: '',
        maxGuests: '2',
        minNights: '1',
        fullRefundDays: '7',
        partialRefundDays: '1',
        partialRefundPercent: '50',
        published: false
    });

    const publicLink = useMemo(() => {
        if (!form.slug) return '';
        return `${window.location.origin}/book/${form.slug}`;
    }, [form.slug]);

    const load = async () => {
        try {
            const [propertiesRes, listingRes, payoutStatusRes] = await Promise.all([
                api.get('/property'),
                api.get(`/direct-booking/host/listings/property/${propertyId}`),
                api.get(`/direct-booking/host/stripe-connect/status?propertyId=${propertyId}`).catch((error) => ({ error }))
            ]);

            if (Array.isArray(propertiesRes?.data)) {
                const currentProperty = propertiesRes.data.find((item) => Number(item.id) === Number(propertyId));
                setProperty(currentProperty || null);
            }

            if (listingRes?.data) {
                const listing = listingRes.data;
                setForm({
                    title: listing.title || '',
                    slug: listing.slug || '',
                    shortDescription: listing.shortDescription || '',
                    longDescription: listing.longDescription || '',
                    heroImageUrl: listing.heroImageUrl || '',
                    ...mapGalleryToPhotoForm(listing.gallery),
                    amenitiesText: (listing.amenities || []).join('\n'),
                    houseRules: listing.houseRules || '',
                    automatedGuestMessage: listing.automatedGuestMessage || '',
                    nightlyRate: listing.nightlyRate === undefined ? '' : String(listing.nightlyRate),
                    maxGuests: listing.maxGuests === undefined ? '2' : String(listing.maxGuests),
                    minNights: listing.minNights === undefined ? '1' : String(listing.minNights),
                    fullRefundDays: listing.fullRefundDays === undefined ? '7' : String(listing.fullRefundDays),
                    partialRefundDays: listing.partialRefundDays === undefined ? '1' : String(listing.partialRefundDays),
                    partialRefundPercent: listing.partialRefundPercent === undefined ? '50' : String(listing.partialRefundPercent),
                    published: !!listing.published
                });
            }

            if (payoutStatusRes?.error) {
                const statusCode = payoutStatusRes.error?.response?.status;
                if (statusCode === 503) {
                    setPayoutStatus({
                        loading: false,
                        state: 'unavailable',
                        text: 'Stripe payouts are unavailable because payments are not configured on the server yet.',
                        account: null,
                        accountId: ''
                    });
                } else {
                    setPayoutStatus({
                        loading: false,
                        state: 'error',
                        text: payoutStatusRes.error?.response?.data?.error || 'Unable to load payout account status.',
                        account: null,
                        accountId: ''
                    });
                }
            } else {
                const statusData = payoutStatusRes?.data || {};
                const connected = !!statusData.connected;
                const hasAccount = !!statusData.stripeAccountId;
                setPayoutStatus({
                    loading: false,
                    state: connected ? 'connected' : hasAccount ? 'action_required' : 'not_connected',
                    text: connected
                        ? 'Stripe payouts are connected. Hosts receive the booking amount minus Stripe processing fees only.'
                        : hasAccount
                            ? 'Stripe account exists but still needs onboarding or verification before payouts can flow.'
                            : 'No Stripe payout account connected for this property yet.',
                    account: statusData.account || null,
                    accountId: statusData.stripeAccountId || ''
                });
            }
        } catch (e) {
            setMessage({ type: 'error', text: 'Failed to load direct booking listing.' });
            setPayoutStatus({ loading: false, state: 'error', text: 'Unable to load payout status.', account: null, accountId: '' });
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyId]);

    useEffect(() => {
        const hash = String(location.hash || '').replace('#', '').trim();
        if (!hash) return;

        const frame = window.requestAnimationFrame(() => {
            const target = document.getElementById(hash);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });

        return () => window.cancelAnimationFrame(frame);
    }, [location.hash, payoutStatus.loading]);

    const onChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const onSave = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            await api.post(`/direct-booking/host/listings/property/${propertyId}`, {
                title: form.title,
                slug: form.slug,
                shortDescription: form.shortDescription,
                longDescription: form.longDescription,
                heroImageUrl: form.heroImageUrl,
                gallery: buildGalleryFromPhotoForm(form),
                amenities: splitLines(form.amenitiesText),
                houseRules: form.houseRules,
                automatedGuestMessage: form.automatedGuestMessage,
                nightlyRate: Number(form.nightlyRate),
                maxGuests: Number(form.maxGuests || 1),
                minNights: Number(form.minNights || 1),
                fullRefundDays: Number(form.fullRefundDays || 0),
                partialRefundDays: Number(form.partialRefundDays || 0),
                partialRefundPercent: Number(form.partialRefundPercent || 0),
                published: !!form.published
            });

            setMessage({ type: 'success', text: 'Direct booking listing saved.' });
        } catch (e) {
            const backendError = e?.response?.data?.error;
            setMessage({ type: 'error', text: backendError || 'Unable to save listing.' });
        } finally {
            setSaving(false);
        }
    };

    const startPayoutSetup = async () => {
        setLaunchingPayoutSetup(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await api.post('/direct-booking/host/stripe-connect/link', {
                propertyId: Number(propertyId)
            });

            const onboardingUrl = String(response?.data?.onboardingUrl || '').trim();
            if (!onboardingUrl) {
                throw new Error('Stripe did not return an onboarding link.');
            }

            window.location.assign(onboardingUrl);
        } catch (e) {
            setMessage({ type: 'error', text: e?.response?.data?.error || e.message || 'Unable to start Stripe payout setup.' });
            setLaunchingPayoutSetup(false);
        }
    };

    const policyPreview = useMemo(() => ({
        fullRefundDays: Number(form.fullRefundDays || 0),
        partialRefundDays: Number(form.partialRefundDays || 0),
        partialRefundPercent: Number(form.partialRefundPercent || 0)
    }), [form.fullRefundDays, form.partialRefundDays, form.partialRefundPercent]);

    return (
        <div className="direct-booking-page">
            <div className="direct-booking-toolbar">
                <button className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>← Back to Property</button>
                <div>
                    <h2>Direct Booking Manager</h2>
                    <p>{property ? `${property.name} · ${property.city}, ${property.state}` : 'Configure listing details and publish a public booking page.'}</p>
                </div>
            </div>

            {message.text && <div className={`direct-booking-alert ${message.type === 'success' ? 'direct-booking-alert-success' : 'direct-booking-alert-error'}`}>{message.text}</div>}

            <form className="direct-booking-form" onSubmit={onSave}>
                <div className="direct-booking-grid">
                    <label>
                        Listing Title
                        <input name="title" value={form.title} onChange={onChange} required />
                    </label>
                    <label>
                        Public URL Slug
                        <input name="slug" value={form.slug} onChange={onChange} required />
                    </label>
                    <label>
                        Nightly Rate (USD)
                        <input name="nightlyRate" type="number" step="0.01" value={form.nightlyRate} onChange={onChange} required />
                    </label>
                    <label>
                        Cleaning Fee (USD)
                        <input
                            type="number"
                            value={property?.cleaningCost !== undefined ? Number(property.cleaningCost).toFixed(2) : '—'}
                            readOnly
                            disabled
                        />
                        <span className="direct-booking-rules-hint" style={{ display: 'block', marginTop: 4 }}>Pulled from the property's standard cleaning cost. Edit it on the property settings page.</span>
                    </label>
                    <label>
                        Max Guests
                        <input name="maxGuests" type="number" min="1" value={form.maxGuests} onChange={onChange} />
                    </label>
                    <label>
                        Minimum Stay (nights)
                        <input name="minNights" type="number" min="1" value={form.minNights} onChange={onChange} />
                    </label>
                </div>

                <div className="direct-booking-settings-grid">
                    <section id="payout-settings" className="direct-booking-settings-card">
                        <h3>Payout Settings</h3>
                        <p className="direct-booking-rules-hint">Connect the currently signed-in host to Stripe for this property so payouts can route to that host's own Stripe account after payment succeeds.</p>
                        <div className={`direct-booking-status-pill is-${getPayoutStatusTone(payoutStatus.state)}`}>
                            {payoutStatus.loading ? 'Loading payout status...' : payoutStatus.text}
                        </div>
                        <p className="direct-booking-meta">Host payout profile: <strong>{user?.display || user?.email || 'Current host'}</strong></p>
                        {payoutStatus.accountId && <p className="direct-booking-meta">Stripe account: {payoutStatus.accountId}</p>}
                        {payoutStatus.account && (
                            <div className="direct-booking-meta-list">
                                <p>Charges enabled: <strong>{payoutStatus.account.chargesEnabled ? 'Yes' : 'No'}</strong></p>
                                <p>Payouts enabled: <strong>{payoutStatus.account.payoutsEnabled ? 'Yes' : 'No'}</strong></p>
                                <p>Account email: <strong>{payoutStatus.account.email || '—'}</strong></p>
                            </div>
                        )}
                        <div className="direct-booking-inline-note">
                            <strong>How this works:</strong> the app keeps one server-side Stripe platform key in the environment, and each host connects a separate Stripe account for payouts through Stripe Connect. Raw host secret keys are not stored in this app.
                        </div>
                        <div className="direct-booking-actions">
                            <button className="btn-primary" type="button" onClick={startPayoutSetup} disabled={launchingPayoutSetup || payoutStatus.state === 'unavailable'}>
                                {launchingPayoutSetup ? 'Opening Stripe...' : payoutStatus.accountId ? 'Update My Stripe Payout Account' : 'Connect My Stripe Payout Account'}
                            </button>
                            <button className="btn-secondary" type="button" onClick={load}>Refresh Status</button>
                        </div>
                    </section>

                    <section className="direct-booking-settings-card">
                        <h3>Cancellation Policy</h3>
                        <p className="direct-booking-rules-hint">These settings control automatic online refunds for guest-initiated cancellations.</p>
                        <div className="direct-booking-grid compact-grid">
                            <label>
                                Full Refund Window (days)
                                <input name="fullRefundDays" type="number" min="0" value={form.fullRefundDays} onChange={onChange} />
                            </label>
                            <label>
                                Partial Refund Window (days)
                                <input name="partialRefundDays" type="number" min="0" value={form.partialRefundDays} onChange={onChange} />
                            </label>
                            <label>
                                Partial Refund Percent
                                <input name="partialRefundPercent" type="number" min="0" max="100" value={form.partialRefundPercent} onChange={onChange} />
                            </label>
                        </div>
                        <div className="direct-booking-policy-preview">
                            <p><strong>Full refund:</strong> more than {policyPreview.fullRefundDays} day{policyPreview.fullRefundDays !== 1 ? 's' : ''} before check-in.</p>
                            <p><strong>Partial refund:</strong> {policyPreview.partialRefundDays}-{policyPreview.fullRefundDays} days before check-in for {policyPreview.partialRefundPercent}% back.</p>
                            <p><strong>No refund:</strong> within {policyPreview.partialRefundDays} day{policyPreview.partialRefundDays !== 1 ? 's' : ''} of check-in.</p>
                        </div>
                    </section>
                </div>

                <label>
                    Hero Image URL
                    <input name="heroImageUrl" value={form.heroImageUrl} onChange={onChange} placeholder="https://..." />
                </label>

                <label>
                    Short Description
                    <input name="shortDescription" value={form.shortDescription} onChange={onChange} maxLength={180} />
                </label>

                <label>
                    Long Description
                    <textarea name="longDescription" value={form.longDescription} onChange={onChange} rows={5} />
                </label>

                <div className="direct-booking-grid">
                    {PHOTO_FIELDS.map((field) => (
                        <label key={field.key}>
                            {field.label} Photo URL
                            <input
                                name={field.key}
                                value={form[field.key] || ''}
                                onChange={onChange}
                                placeholder="https://..."
                            />
                        </label>
                    ))}
                    <label>
                        Amenities (one per line)
                        <textarea name="amenitiesText" value={form.amenitiesText} onChange={onChange} rows={6} />
                    </label>
                </div>

                <label>
                    House Rules
                    <textarea name="houseRules" value={form.houseRules} onChange={onChange} rows={4} />
                </label>

                <label>
                    Automated Guest Message
                    <textarea
                        name="automatedGuestMessage"
                        value={form.automatedGuestMessage}
                        onChange={onChange}
                        rows={5}
                        placeholder="Example: WiFi: LakeHouseGuest / sunset2026. Check-in is after 4 PM. Please park in the left driveway and use the keypad code we send on arrival day."
                    />
                    <span className="direct-booking-rules-hint" style={{ display: 'block', marginTop: 4 }}>
                        This message is automatically sent in the first chat message after a guest books. Use it for Wi-Fi details, parking instructions, entry info, or arrival notes.
                    </span>
                </label>

                <label className="direct-booking-publish">
                    <input name="published" type="checkbox" checked={form.published} onChange={onChange} />
                    Publish listing publicly
                </label>

                <div className="direct-booking-actions">
                    <button className="btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Listing'}</button>
                    {publicLink && <a className="btn-secondary direct-booking-link-btn" href={publicLink} target="_blank" rel="noreferrer">Open Public Page</a>}
                </div>
            </form>

            <DirectBookingPricingRules propertyId={propertyId} baseRate={Number(form.nightlyRate) || 0} />
        </div>
    );
};

export default DirectBookingManagerPage;
